import React, {SyntheticEvent, useContext, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Typography,
    useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {ExpiredActionProps, IExpiredPracticeDetailForm} from "../../models/models";
import {FormDateTimePicker, FormTextField} from "../../common/form-fields-wrappers";
import DetailActionHistory from "../detail-action-history/DetailActionHistory";
import {useNavigate} from "react-router-dom";
import { useRecoilValue, useSetRecoilState} from "recoil";
import { excludedExpiredRecoveratorAttachmentState, expiredActionPropsState, practiceDetail, recoveryEntrustmentDetailState} from "../../state";
import { ExpiredInsertionAction } from "../../config";
import CloseActionButton from "../action/close/CloseActionButton";
import WatchlistActionButton from "../action/watchlist/WatchlistActionButton";
import RecoveryCompanyActionButton from "../action/recovery-company/RecoveryCompanyActionButton";
import ActionInsertion from "../../action-insertion/ActionInsertion";
import { useExpiredPracticeActionHistoryQueryObserver } from "../../services/queryObserver";
import CustomLoader from "../../../../util/component/CustomLoader";
import ActionMenuButton from "../../action-insertion/menu-button/ActionMenuButton";
import { useMutation } from "@tanstack/react-query";
import { insertExpiredFromRaCode } from "../../services/http.services";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import UploadAttachment from "../../common/upload-attachment/UploadAttachment";
import RecoveryEntrustment from "../../recovery-entrustment/RecoveryEntrustment";
import { Close } from "@mui/icons-material";
import WarningLetter from "../../warning-letter/WarningLetter";

interface PracticeDetailProps {
    isInsertMode: boolean,
    handleUploadPostAction?: Function
}

const PracticeDetail = ({ isInsertMode, handleUploadPostAction }: PracticeDetailProps) => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const navigate = useNavigate();
    const { dispatchGlobalData } = useContext(GlobalContext);

    const expiredPracticeDetailForm = useFormContext<IExpiredPracticeDetailForm>();

    const [openWarningLetterDialog, setOpenWarningLetterDialog] = useState(false);
    const [openRecoveryEntrustmentDialog, setOpenRecoveryEntrustmentDialog] = useState(false);
    const [actionDialogProps, setActionDialogProps] = useState<{
        title: string,
        open: boolean
    }>({
        title: '',
        open: false
    });

    const prDetail = useRecoilValue(practiceDetail);
    const setExpiredActionProps = useSetRecoilState(expiredActionPropsState);
    const setRecoveryEntrustmentDetail = useSetRecoilState(recoveryEntrustmentDetailState);
    const setRecoveryEntrustmentExcludedAttachments = useSetRecoilState(excludedExpiredRecoveratorAttachmentState);

    const actionHistoryQueryObserver = useExpiredPracticeActionHistoryQueryObserver(prDetail?.expiredCode);

    const handleSaveExpired = (event: React.MouseEvent) => {
        event.stopPropagation();

        const raCode = expiredPracticeDetailForm.getValues("detail.raCode");
        if(!!raCode)
            insertExpiredFromRaCodeMutation.mutate(raCode);
    }

    const handleOpenBooking = (event: React.MouseEvent) => {
        event.stopPropagation();

        window.open(`/booking/view/${prDetail.bookingCode}`, '_blank');

    }

    const handleOpenRA = (event: React.MouseEvent) => {
        event.stopPropagation();

        window.open(`/network/ra-detail/${prDetail.raCode}`, '_blank');
    }

    const handleInsertAction = async (actionType: any) => {
        var expActionProps: ExpiredActionProps | null = null;
        var openActionDialog: any = null;

        const expDetail = expiredPracticeDetailForm.getValues("detail");

        switch(actionType) { 
            case ExpiredInsertionAction.SMS1: 
            case ExpiredInsertionAction.SMS2:
            case ExpiredInsertionAction.SMS3:
            case ExpiredInsertionAction.NEW_SMS:
            case ExpiredInsertionAction.RECORD_CALL:
            case ExpiredInsertionAction.IN_CAR_REPAIR_SHOP:
            case ExpiredInsertionAction.IN_DMS:
            case ExpiredInsertionAction.NO_VEHICLE:
            case ExpiredInsertionAction.POSITIVE_RECOVERY:
            case ExpiredInsertionAction.NEGATIVE_RECOVERY:
            case ExpiredInsertionAction.MISAPPROPRIATION:
            case ExpiredInsertionAction.THEFT:
            case ExpiredInsertionAction.OPERATOR_NOTES:
                expActionProps = {
                    actionType,
                    expiredCodes: !!expDetail.expiredCode ? [expDetail.expiredCode] : [],
                    raDetails: {
                        raNumber: expDetail.raCode,
                        raDate: expDetail.createdDatetime,
                        rentalType: expDetail.rentalType || '',
                        plateNumber: expDetail.plate,
                        delayDays: expDetail.delayDays,
                        clientId: expDetail.customerSapId,
                        customerNameSurname: expDetail.customerNameSurname,
                        phoneNumber: expDetail.customerMobile,
                        companyName: expDetail.companyName,
                        acriss: expDetail.effectiveAcriss,
                    },
                    returnDetails: {
                        date: expDetail.effectiveCheckOutDate,
                        location: expDetail.effectiveCheckOutLocationLabel
                    }
                };

                openActionDialog = () => setActionDialogProps({
                    title: actionType,
                    open: true
                });

                break;

            case ExpiredInsertionAction.RECOVERY:
                expActionProps = {
                    actionType,
                    expiredCodes: !!expDetail.expiredCode ? [expDetail.expiredCode] : [],
                    raDetails: {
                        rentalType: expDetail.rentalType || ''
                    }
                };

                openActionDialog = () => setOpenRecoveryEntrustmentDialog(true);

                break;

            case ExpiredInsertionAction.WARNING_LETTER:
                expActionProps = {
                    actionType,
                    expiredCodes: !!expDetail.expiredCode ? [expDetail.expiredCode] : [],
                    raDetails: {
                        clientId: expDetail.customerSapId,
                        companyName: expDetail.companyName,
                        customerNameSurname: expDetail.customerNameSurname,
                        customerEmail: expDetail.customerEmail
                    }
                };

                openActionDialog = () => setOpenWarningLetterDialog(true);

                break;
        }

        if(!!expActionProps && !!openActionDialog) {
            await setExpiredActionProps(expActionProps);
            openActionDialog();
        }
    }

    const handleActionDialogClose = () => {
        
        setActionDialogProps({
            ...actionDialogProps,
            open: false
        });
        setExpiredActionProps(null);
    }

    const handleRecoveryEntrustmentDialogClose = () => {

        setOpenRecoveryEntrustmentDialog(false);
        setExpiredActionProps(null);
        setRecoveryEntrustmentDetail(null);
        setRecoveryEntrustmentExcludedAttachments([]);
    }

    const handleWarningLetterDialogClose = () => {
        
        setOpenWarningLetterDialog(false);
        setExpiredActionProps(null);
    }

    const insertExpiredFromRaCodeMutation = useMutation({
        mutationFn: (raCode: string) => {
            return insertExpiredFromRaCode(raCode);
        },
        onSuccess: (response) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('common.message.success'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });

            const newExpiredCode = response?.data?.expiredCode;
            if(newExpiredCode)
                navigate(`/expired-management/practice-detail/${newExpiredCode}`);
        },
        onError: (error: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`common.message.error.insertExpiredFromRaCode.${error?.response?.status}`, t('common.message.error.tryLater')),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });

        }
    });

    return (
        <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
            <Grid container columnSpacing={2} sx={{mb: theme.spacing(2)}}>
                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.generalInfo')}
                    </Typography>
                </Grid>
                {!isInsertMode &&
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IExpiredPracticeDetailForm>
                            name='detail.expiredCode'
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.expiredCode')}
                            fullWidth
                            disabled
                        />
                    </Grid>
                }
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.status'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.status')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.rentalType'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.rentalType')}
                        fullWidth
                        disabled
                    />
                </Grid>
                {!isInsertMode &&
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormDateTimePicker<IExpiredPracticeDetailForm>
                            name="detail.createdDatetime"
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.createdDatetime')}
                            disabled
                        />
                    </Grid>
                }
                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.raInfo')}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.raCode'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.raCode')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.delayDays'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.delayDays')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.bookingCode'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.bookingCode')}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.productInfo')}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.segment"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.segment')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.category"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.category')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.product'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.product')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.rate'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.rate')}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.vehicleInfo')}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.billingAcriss"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.billingAcriss')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.effectiveAcriss"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.effectiveAcriss')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.vehicleModel'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.vehicleModel')}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.plate"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.plate')}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.checkoutCheckinInfo')}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormDateTimePicker<IExpiredPracticeDetailForm>
                        name="detail.effectiveCheckOutDate"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.effectiveCheckOutDate')}
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.effectiveCheckOutLocationLabel"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.effectiveCheckOutLocation')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormDateTimePicker<IExpiredPracticeDetailForm>
                        name="detail.expectedCheckInDate"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.expectedCheckInDate')}
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name="detail.expectedeCheckInLocationLabel"
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.expectedCheckInLocation')}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                    <Typography variant="bodyLarge" component="div">
                        {t('expiredDetail.label.customerInfo')}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.customerSapId'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.customerSapId')}
                        fullWidth
                        disabled
                    />
                </Grid>

                {
                    !!prDetail?.companyName ?
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IExpiredPracticeDetailForm>
                                name='detail.companyName'
                                control={expiredPracticeDetailForm.control}
                                label={t('expiredDetail.form.companyName')}
                                fullWidth
                                disabled
                            />
                        </Grid>:
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IExpiredPracticeDetailForm>
                                name='detail.customerNameSurname'
                                control={expiredPracticeDetailForm.control}
                                label={t('expiredDetail.form.customerNameSurname')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                }

                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.customerEmail'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.customerEmail')}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                    <FormTextField<IExpiredPracticeDetailForm>
                        name='detail.customerMobile'
                        control={expiredPracticeDetailForm.control}
                        label={t('expiredDetail.form.customerMobile')}
                        fullWidth
                        disabled
                    />
                </Grid>

                {!isInsertMode &&
                    <>
                        <Grid item xs={12} sx={{mt: theme.spacing(4)}}>
                            <Typography variant="bodyLarge" component="div">
                                {t('expiredDetail.label.recoveryCompanyInfo')}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IExpiredPracticeDetailForm>
                                name='detail.recoveryCompany'
                                control={expiredPracticeDetailForm.control}
                                label={t('expiredDetail.form.recoveryCompanyName')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IExpiredPracticeDetailForm>
                                name='detail.recoveryCompanyEmail'
                                control={expiredPracticeDetailForm.control}
                                label={t('expiredDetail.form.recoveryCompanyEmail')}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IExpiredPracticeDetailForm>
                                name='detail.competanceAmount'
                                control={expiredPracticeDetailForm.control}
                                label={t('expiredDetail.form.competenceAmount')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                    </>
                }
            </Grid>
            
            { isInsertMode && 
                <Box sx={{py: theme.spacing(2), display: "flex", justifyContent: "flex-end"}}>
                    <Button 
                        sx={{ml: theme.spacing(1)}}
                        disabled={!prDetail?.raCode}
                        onClick={handleSaveExpired}
                    >
                        {t('common.button.save')}
                    </Button>
                </Box>

            }
            { !isInsertMode &&
                <>
                    <Box sx={{pt: theme.spacing(4)}}>
                        <DetailActionHistory />
                    </Box>

                    <Box sx={{py: theme.spacing(2), display: "flex", justifyContent: "flex-end"}}>
                        <Button sx={{ml: theme.spacing(1)}} onClick={handleOpenBooking}>
                            {t('expiredDetail.button.viewBooking')}
                        </Button>
                        <Button sx={{ml: theme.spacing(1)}} onClick={handleOpenRA}>
                            {t('expiredDetail.button.viewRa')}
                        </Button>

                        <UploadAttachment expiredCode={prDetail?.expiredCode} variant={'contained'} label={'common.button.uploadAttachment'} showSnackbar postAction={handleUploadPostAction}/>

                        <ActionMenuButton handleInsertAction={handleInsertAction}/>

                        <RecoveryCompanyActionButton />
                        <WatchlistActionButton />
                        <CloseActionButton />
                    </Box>

                    <Dialog open={actionDialogProps.open} fullWidth maxWidth={"xl"}>
                        <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Typography variant={"body"}>{t(`common.actions.dialog.title.${actionDialogProps.title}`)}</Typography>

                            <IconButton 
                                onClick={handleActionDialogClose} 
                                sx={{ml: theme.spacing(4), mr: "0px"}}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <ActionInsertion 
                                onConfirm={() => {
                                    handleActionDialogClose();
                                    actionHistoryQueryObserver.refetch();
                                }}
                                onClose={handleActionDialogClose}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={openRecoveryEntrustmentDialog} fullWidth maxWidth={"xl"} >
                        <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Typography>{t('common.actions.dialog.title.RECUPERATORE')}</Typography>

                            <IconButton 
                                onClick={handleRecoveryEntrustmentDialogClose} 
                                sx={{ml: theme.spacing(4), mr: "0px"}}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent>
                            <RecoveryEntrustment 
                                onConfirm={() => {
                                    handleRecoveryEntrustmentDialogClose();
                                    actionHistoryQueryObserver.refetch();
                                }}
                                onClose={handleRecoveryEntrustmentDialogClose}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={openWarningLetterDialog} fullWidth maxWidth={"xl"}>
                        <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Typography>{t('common.actions.dialog.title.DIFFIDA')}</Typography>

                            <IconButton 
                                onClick={handleWarningLetterDialogClose} 
                                sx={{ml: theme.spacing(4), mr: "0px"}}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent>
                            <WarningLetter 
                                onConfirm={() => {
                                    handleWarningLetterDialogClose();
                                    actionHistoryQueryObserver.refetch();
                                }}
                                onClose={handleWarningLetterDialogClose}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            }

            <CustomLoader loading={actionHistoryQueryObserver.isFetching || insertExpiredFromRaCodeMutation.isLoading} />
        </Paper>
    )
}

export default PracticeDetail;